import { of } from 'rxjs';
import { ApiDataSource } from '../api.data-source';
import { debounceTime, finalize, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { CustomerExperienceChannelSettingsService, CustomerExperienceQuickReply, CustomerExperienceQuickReplyService } from '@core/api';

export class CustomerExperienceQuickReplyDataSource extends ApiDataSource<CustomerExperienceQuickReply> {

    public isOpenedNewTab = false;

    public $autoReplyOptions = of([
        { key: true, value: this.translate.instant('GENERAL.TRUE') },
        { key: false, value: this.translate.instant('GENERAL.FALSE') }
    ]);

    public $status = of([
        { key: true, value: this.translate.instant('GENERAL.ACTIVE') },
        { key: false, value: this.translate.instant('GENERAL.PASSIVE') }
    ]);

    public $users = this.quickReplyService.listCreatedUsers({ filter: {} }).pipe(
        map(response => response.data),
        map(users => users.map(item => {
            return {
                key: item.userId,
                value: item.displayName
            };
        }))
    );

    public $channelTypes = this.channelService.getChannelTypes().pipe(
        map(response => response.data),
        map(chTypes => chTypes.map(channelType => {
            return {
                key: channelType.cxChannelTypeId,
                value: channelType.name
            };
        }))
    );

    public $channels = this.channelService.search({
        orderBy: 'cxChannelName',
        orderType: 'ASC',
        page: 1,
        pageSize: 1000,
        filter: {}
    }).pipe(
        map(response => response.data.results),
        map(channels => channels.sort((a, b) => a.name.localeCompare(b.name))),
        map(channels => channels.map(channel => {
            return {
                key: channel.cxChannelId,
                value: channel.name
            };
        }))
    );

    private filterReq: any;

    constructor(
        private quickReplyService: CustomerExperienceQuickReplyService,
        private channelService: CustomerExperienceChannelSettingsService,
        private translate: TranslateService,
        protected initialFilter?: any,
    ) {
        super(initialFilter);
    }

    getFilter() {
        return this.filterReq;
    }

    load(): void {
        if (!this.isOpenedNewTab) {
            // Init filter with data source's default filter
            const filterValue: any = { ...this.initialFilter, ...this.filter };

            // If filter keyword exists, filter data
            if (this.keyword) {
                filterValue.searchText = this.keyword;
            }

            // Update loading state
            this.loadingSubject.next(true);

            // Create request parameters
            const request = this.getRequest();
            if (!request.orderType) {
                request.orderType = 'ASC';
                request.orderBy = 'orderBy';
            }

            if (this.paginator?.pageSize) {
                request.pageSize = this.paginator.pageSize;
            }

            // Add filters to request
            request.filter = filterValue;

            this.filterReq = { ...request };
            // Fetch data
            this.quickReplyService
                .search(request)
                .pipe(
                    debounceTime(400),
                    finalize(() => this.loadingSubject.next(false))
                ).subscribe(response => {
                    // Update count and data subjects
                    this.dataSubject.next(response.data.results);
                    this.dataCountSubject.next(response.data.rowCount);

                    // Update data source's empty based row count
                    this.empty = response.data.rowCount === 0;
                });
        }

        return this.loadingSubject.next(false);
    }
}
